import { Helmet } from "react-helmet";
import { useState } from "react";
import {
  FaMagnifyingGlass,
  FaBoxOpen,
  FaChevronLeft,
  FaChevronRight,
  FaAnglesLeft,
  FaAnglesRight,
  FaFilter,
  FaFileExport,
  FaTriangleExclamation,
  FaBoxArchive,
  FaCircleCheck,
  FaXmark,
  FaPlus,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useHookLogisticMonitoring } from "../../../../hooks/useHookLogisticMonitoring";
import { CreateNewLogisticItemModal } from "./create";

/* ============================================
   KOMPONEN: Skeleton Bar
   ============================================ */
const SkeletonRow = ({ cols = 8 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3.5 bg-slate-700/60 rounded-md" style={{ width: `${35 + Math.random() * 55}%` }} />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div className="rounded-2xl p-5 animate-pulse" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-700/60" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/60 rounded-md w-32" />
          <div className="h-3 bg-slate-700/40 rounded-md w-20" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-14" />
    </div>
    <div className="grid grid-cols-2 gap-3 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
      {[...Array(4)].map((_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-2.5 bg-slate-700/30 rounded w-16" />
          <div className="h-3.5 bg-slate-700/50 rounded w-20" />
        </div>
      ))}
    </div>
  </div>
);

/* ============================================
   KOMPONEN: StokBadge (Menggunakan min_stock dinamis)
   ============================================ */
const StokBadge = ({ stok, minStock = 10 }) => {
  if (stok === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/25">
        <FaXmark size={10} />
        HABIS
      </span>
    );
  }
  if (stok < minStock) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">
        <FaTriangleExclamation size={10} />
        {stok}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      <FaCircleCheck size={10} />
      {stok}
    </span>
  );
};

/* ============================================
   KOMPONEN: SummaryCard
   ============================================ */
const SummaryCard = ({ icon, label, value, accent = "cyan", pulse = false, solid = false }) => {
  const accents = {
    cyan: { bg: solid ? "bg-cyan-600" : "bg-cyan-500/10", border: solid ? "border-cyan-600" : "border-cyan-500/20", text: solid ? "text-white" : "text-cyan-400", icon: solid ? "text-cyan-200" : "text-cyan-500", sub: solid ? "text-cyan-100" : "text-slate-500" },
    blue: { bg: solid ? "bg-blue-600" : "bg-blue-500/10", border: solid ? "border-blue-600" : "border-blue-500/20", text: solid ? "text-white" : "text-blue-400", icon: solid ? "text-blue-200" : "text-blue-500", sub: solid ? "text-blue-100" : "text-slate-500" },
    amber: { bg: solid ? "bg-amber-600" : "bg-amber-500/10", border: solid ? "border-amber-600" : "border-amber-500/20", text: solid ? "text-white" : "text-amber-400", icon: solid ? "text-amber-200" : "text-amber-500", sub: solid ? "text-amber-100" : "text-slate-500" },
    orange: { bg: solid ? "bg-orange-600" : "bg-orange-500/10", border: solid ? "border-orange-600" : "border-orange-500/20", text: solid ? "text-white" : "text-orange-400", icon: solid ? "text-orange-200" : "text-orange-500", sub: solid ? "text-orange-100" : "text-slate-500" },
    red: { bg: solid ? "bg-red-600" : "bg-red-500/10", border: solid ? "border-red-600" : "border-red-500/20", text: solid ? "text-white" : "text-red-400", icon: solid ? "text-red-200" : "text-red-500", sub: solid ? "text-red-100" : "text-slate-500" },
  };
  const a = accents[accent];

  return (
    <div className={`relative p-4 rounded-2xl border ${a.bg} ${a.border} ${pulse ? "animate-pulse" : ""}`}>
      <div className={`mb-2 ${a.icon}`}>{icon}</div>
      <p className={`text-2xl font-bold ${a.text}`}>{value.toLocaleString("id-ID")}</p>
      <p className={`text-xs font-medium mt-0.5 ${a.sub}`}>{label}</p>
    </div>
  );
};

/* ============================================
   KOMPONEN: Select Dark (inline)
   ============================================ */
const SelectDark = ({ value, onChange, options, placeholder }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((opt) => (
      <option key={typeof opt === "string" ? opt : opt.value} value={typeof opt === "string" ? opt : opt.value}>
        {typeof opt === "string" ? opt : opt.label}
      </option>
    ))}
  </select>
);

/* ============================================
   KOMPONEN UTAMA: LogisticMonitoring
   ============================================ */
export const LogisticMonitoring = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);

  const {
    data,
    meta,
    loading,
    params,
    setParams,
    stats,
    categories,
    refreshData,
  } = useHookLogisticMonitoring();

  /* ---------- Handlers Sync Params ---------- */
  const handleSearchChange = (val) => setParams((p) => ({ ...p, keyword: val, page: 1 }));
  const handleCategoryChange = (val) => setParams((p) => ({ ...p, category_id: val, page: 1 }));
  const handleStatusChange = (val) => setParams((p) => ({ ...p, stock_status: val, page: 1 }));
  const handlePageChange = (page) => setParams((p) => ({ ...p, page }));

  const resetFilter = () => {
    setParams({
      keyword: "",
      category_id: "",
      stock_status: "",
      page: 1,
      per_page: 10,
    });
  };

  /* ---------- Pagination Logic ---------- */
  const getPages = () => {
    if (!meta) return [];
    const total = meta.last_page;
    const current = meta.current_page;
    const delta = 2;
    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  };

  /* ---------- Export CSV ---------- */
  const handleExportCSV = () => {
    const headers = ["Kategori", "Nama Barang", "Jenis", "Tidak Digunakan", "Terpakai", "Rusak", "Stok", "Status"];
    const rows = data.map((item) => [
      item.category?.name || "-",
      item.name,
      item.type || "-",
      item.unused_stock,
      item.used_stock,
      item.damaged_stock,
      item.unused_stock,
      item.unused_stock === 0 ? "HABIS" : item.unused_stock < item.min_stock ? "KRITIS" : "AMAN",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `monitoring-barang-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- Row hover class ---------- */
  const getRowHover = (stok, minStock) => {
    if (stok === 0) return { enter: "rgba(239, 68, 68, 0.06)", leave: "rgba(239, 68, 68, 0.03)" };
    if (stok < minStock) return { enter: "rgba(239, 68, 68, 0.05)", leave: "rgba(239, 68, 68, 0.02)" };
    return { enter: "rgba(0, 180, 216, 0.04)", leave: "transparent" };
  };

  const getRowBg = (stok, minStock) => {
    if (stok === 0) return "rgba(239, 68, 68, 0.03)";
    if (stok < minStock) return "rgba(239, 68, 68, 0.02)";
    return "transparent";
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "kategori", label: "Kategori", align: "left" },
    { key: "nama", label: "Nama Barang", align: "left" },
    { key: "jenis", label: "Jenis", align: "left" },
    { key: "tidakDigunakan", label: "Tdk Digunakan", align: "center" },
    { key: "terpakai", label: "Terpakai", align: "center" },
    { key: "rusak", label: "Rusak", align: "center" },
    { key: "stok", label: "Stok", align: "center", width: "120px" },
  ];

  /* ---------- Filter active count ---------- */
  const activeFilterCount = (params.category_id ? 1 : 0) + (params.stock_status ? 1 : 0);

  return (
    <>
      <Helmet>
        <title>Monitoring Barang - Radius Trend</title>
        <meta name="description" content="Pantau stok perangkat dan barang ISP / RT-RW Net secara real-time." />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Monitoring Barang
            </h2>
            {meta && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                {meta.total} data
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <BtnDefault onClick={() => setOpenModalCreate(true)}>
              <span className="hidden lg:flex items-center gap-2">
                <FaPlus className="text-xs" />
                Tambah Data
              </span>
              <FaFileExport className="lg:hidden" />
            </BtnDefault>
            <BtnDefault onClick={handleExportCSV}>
              <span className="hidden lg:flex items-center gap-2">
                <FaFileExport className="text-xs" />
                Export CSV
              </span>
              <FaFileExport className="lg:hidden" />
            </BtnDefault>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">

          {/* ===== Summary Cards ===== */}
          <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" aria-label="Ringkasan stok barang">
            <SummaryCard icon={<FaBoxOpen size={20} />} label="Total Stok" value={stats.total_stock} accent="cyan" />
            <SummaryCard icon={<FaCircleCheck size={20} />} label="Terpakai" value={stats.used} accent="blue" />
            <SummaryCard icon={<FaBoxArchive size={20} />} label="Tidak Digunakan" value={stats.unused} accent="amber" />
            <SummaryCard icon={<FaXmark size={20} />} label="Rusak" value={stats.damaged} accent="orange" />
            <SummaryCard icon={<FaTriangleExclamation size={20} />} label="Stok Kritis" value={stats.critical} accent="red" pulse={stats.critical > 0} />
            <SummaryCard icon={<FaXmark size={20} />} label="Stok Habis" value={stats.empty} accent="red" solid pulse={stats.empty > 0} />
          </section>

          {/* ===== Alert Banner ===== */}
          {(stats.critical > 0 || stats.empty > 0) && (
            <div className="flex items-start gap-3 px-4 py-3.5 rounded-2xl bg-red-500/8 border border-red-500/20">
              <FaTriangleExclamation className="text-red-400 mt-0.5 shrink-0" size={16} />
              <div>
                <p className="text-sm font-bold text-red-300">Peringatan Stok!</p>
                <p className="text-sm text-red-400/80 mt-0.5">
                  Terdapat{" "}
                  <span className="font-bold text-red-300">{stats.critical} item</span> dengan stok di bawah batas minimum dan{" "}
                  <span className="font-bold text-red-300">{stats.empty} item</span> dengan stok habis. Segera lakukan pengadaan.
                </p>
              </div>
            </div>
          )}

          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Filter dan pencarian"
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label htmlFor="search-barang" className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                  Cari Data
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    id="search-barang"
                    type="text"
                    placeholder="Nama barang, jenis..."
                    value={params.keyword}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Filter</span>
                  {activeFilterCount > 0 && (
                    <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-cyan-600 text-white text-[10px] font-bold rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <SelectDark
                    value={params.category_id}
                    onChange={handleCategoryChange}
                    options={categories.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Semua Kategori"
                  />
                  <SelectDark
                    value={params.stock_status}
                    onChange={handleStatusChange}
                    options={[
                      { value: "", label: "Semua Status" },
                      { value: "critical", label: "Kritis (< Min Stok)" },
                      { value: "empty", label: "Habis (0)" },
                    ]}
                  />
                  <button
                    onClick={resetFilter}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 border border-slate-600/40 bg-slate-800/60 hover:text-slate-200 hover:border-slate-500/50 transition-all"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel monitoring barang"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Head */}
                <thead>
                  <tr className="border-b" style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {/* Loading */}
                  {loading && (
                    <>
                      {[...Array(5)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && data?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                            <FaBoxOpen className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">Tidak ada data ditemukan</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {params.keyword || params.category_id || params.stock_status
                                ? "Coba ubah filter atau kata kunci pencarian"
                                : "Belum ada data barang yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    data?.map((item, index) => {
                      const stok = item.unused_stock;
                      const minStock = item.min_stock;
                      const hover = getRowHover(stok, minStock);
                      return (
                        <tr
                          key={item.id}
                          className="transition-colors duration-150 cursor-pointer"
                          style={{ background: getRowBg(stok, minStock) }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = hover.enter; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = hover.leave; }}
                          onClick={() => navigate(`/dashboard/logistic/monitoring/detail/${item.id}`)}
                        >
                          <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                            {meta.from + index}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2.5 py-0.5 bg-slate-700/50 text-slate-300 text-xs font-semibold rounded-lg border border-slate-600/30">
                              {item.category?.name || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-semibold ${stok < minStock ? "text-red-300" : "text-gray-100"}`}>
                              {item.name}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-400">{item.type || "-"}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-slate-300 font-medium">{stok}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-cyan-400/80 font-medium">{item.used_stock}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-medium ${item.damaged_stock > 0 ? "text-orange-400" : "text-slate-500"}`}>
                              {item.damaged_stock}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <StokBadge stok={stok} minStock={minStock} />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <p className="text-xs text-slate-500">
                  Menampilkan{" "}
                  <span className="text-slate-300 font-medium">{meta.from}</span> sampai{" "}
                  <span className="text-slate-300 font-medium">{meta.to}</span> dari{" "}
                  <span className="text-slate-300 font-medium">{meta.total}</span>
                </p>

                <nav className="flex items-center gap-1" aria-label="Navigasi halaman">
                  <button onClick={() => handlePageChange(1)} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all" aria-label="Halaman pertama">
                    <FaAnglesLeft className="text-xs" />
                  </button>
                  <button onClick={() => handlePageChange(meta.current_page - 1)} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all" aria-label="Halaman sebelumnya">
                    <FaChevronLeft className="text-xs" />
                  </button>

                  {getPages().map((p, i) =>
                    p === "..." ? (
                      <span key={`dot-${i}`} className="px-2 text-slate-600 text-sm">···</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${meta.current_page === p
                            ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/25"
                            : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"
                          }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button onClick={() => handlePageChange(meta.current_page + 1)} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all" aria-label="Halaman berikutnya">
                    <FaChevronRight className="text-xs" />
                  </button>
                  <button onClick={() => handlePageChange(meta.last_page)} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all" aria-label="Halaman terakhir">
                    <FaAnglesRight className="text-xs" />
                  </button>
                </nav>
              </div>
            )}
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3" aria-label="Daftar barang">
            {/* Loading */}
            {loading && (
              <>
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
              </>
            )}

            {/* Empty */}
            {!loading && data?.length === 0 && (
              <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                  <FaBoxOpen className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">Tidak ada data ditemukan</p>
                <p className="text-xs text-slate-500 mt-1">
                  {params.keyword || params.category_id || params.stock_status ? "Coba ubah filter pencarian" : "Belum ada data barang"}
                </p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              data?.map((item) => {
                const stok = item.unused_stock;
                const minStock = item.min_stock;
                return (
                  <article
                    key={item.id}
                    className="rounded-2xl p-4 active:scale-[0.98] transition-all duration-200 cursor-pointer"
                    style={{
                      background: stok === 0 ? "rgba(239, 68, 68, 0.04)" : stok < minStock ? "rgba(239, 68, 68, 0.03)" : "rgba(15, 23, 42, 0.5)",
                      border: stok === 0 ? "1px solid rgba(239, 68, 68, 0.2)" : stok < minStock ? "1px solid rgba(239, 68, 68, 0.12)" : "1px solid rgba(51, 65, 85, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = stok < minStock ? "rgba(239, 68, 68, 0.25)" : "rgba(0, 180, 216, 0.2)";
                      e.currentTarget.style.background = stok < minStock ? "rgba(239, 68, 68, 0.06)" : "rgba(15, 23, 42, 0.7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = stok === 0 ? "rgba(239, 68, 68, 0.2)" : stok < minStock ? "rgba(239, 68, 68, 0.12)" : "rgba(51, 65, 85, 0.3)";
                      e.currentTarget.style.background = stok === 0 ? "rgba(239, 68, 68, 0.04)" : stok < minStock ? "rgba(239, 68, 68, 0.03)" : "rgba(15, 23, 42, 0.5)";
                    }}
                    onClick={() => navigate(`/dashboard/logistic/monitoring/detail/${item.id}`)}
                  >
                    {/* Top: Nama + Stok */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
                          style={{
                            background: stok < minStock
                              ? "linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05))"
                              : "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                            border: stok < minStock ? "1px solid rgba(239, 68, 68, 0.15)" : "1px solid rgba(0, 180, 216, 0.15)",
                            color: stok < minStock ? "#f87171" : "#48CAE4",
                          }}
                        >
                          {item.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className={`font-semibold leading-tight text-sm ${stok < minStock ? "text-red-300" : "text-gray-100"}`}>
                            {item.name}
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">{item.type || "-"}</p>
                        </div>
                      </div>
                      <StokBadge stok={stok} minStock={minStock} />
                    </div>

                    {/* Bottom: Details */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Kategori</p>
                        <p className="text-sm text-slate-300 mt-0.5 truncate">{item.category?.name || "-"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Terpakai</p>
                        <p className="text-sm text-cyan-400/80 font-medium mt-0.5">{item.used_stock}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Tidak Digunakan</p>
                        <p className="text-sm text-slate-300 mt-0.5">{stok}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Rusak</p>
                        <p className={`text-sm font-medium mt-0.5 ${item.damaged_stock > 0 ? "text-orange-400" : "text-slate-500"}`}>{item.damaged_stock}</p>
                      </div>
                    </div>
                  </article>
                );
              })}

            {/* Mobile Pagination */}
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between pt-3 pb-6 px-1" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                <button
                  onClick={() => handlePageChange(meta.current_page - 1)}
                  disabled={meta.current_page === 1}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                >
                  <FaChevronLeft className="text-xs" />
                  Sebelumnya
                </button>

                <span className="text-sm font-medium text-slate-400">
                  <span className="text-cyan-400">{meta.current_page}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  {meta.last_page}
                </span>

                <button
                  onClick={() => handlePageChange(meta.current_page + 1)}
                  disabled={meta.current_page === meta.last_page}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                >
                  Berikutnya
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </section>

          {/* ===== Legend ===== */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-1">
            <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Keterangan:</span>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/30 border border-emerald-500/40" />
              <span className="text-xs text-slate-400">Aman (≥ Min Stok)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/30" />
              <span className="text-xs text-slate-400">Kritis (&lt; Min Stok)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/40 border border-red-500/50" />
              <span className="text-xs text-slate-400">Habis (0)</span>
            </div>
          </div>

        </main>
      </DashboardLayout>

      <CreateNewLogisticItemModal
        openModal={openModalCreate}
        setOpenModal={setOpenModalCreate}
        refresh={refreshData}
      />
    </>
  );
};

